/**
 * Delete confirmation for one or many admin users, rendered inside the
 * shared Modal (do not edit Modal.tsx). Replaces the shared MUI
 * DestructiveDialog on the Users list (DeleteUserButton on the details page
 * keeps DestructiveDialog until the details page migrates). Success toasts
 * and closes; failure shows in Modal's error alert. Copy follows the ids
 * being deleted (`variables`), not the live prop, because the page clears
 * its selection in onDeleted. Modal keeps Cancel enabled during isLoading,
 * so requestClose ignores it mid-delete.
 */
import { deleteUser } from "@/api";
import { Modal } from "@/components/shared/Modal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

export interface DeleteUsersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userIds: number[];
    /** Called once after every id was deleted (e.g. to clear selection). */
    onDeleted?: () => void;
}

export function DeleteUsersDialog({
    open,
    onOpenChange,
    userIds,
    onDeleted,
}: DeleteUsersDialogProps) {
    const queryClient = useQueryClient();

    const { mutate, reset, isPending, isError, variables } = useMutation({
        mutationFn: (ids: number[]) =>
            Promise.all(ids.map((id) => deleteUser(id))),
        onSuccess: (_data, ids) => {
            toast.success(
                ids.length === 1
                    ? "User deleted"
                    : `${ids.length} users deleted`
            );
            onDeleted?.();
            onOpenChange(false);
        },
        // Settled, not success: with Promise.all some deletes may have landed
        // before one failed, and the list must not keep showing those rows.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        },
    });

    // A reopened dialog starts clean, not on the last attempt's error.
    useEffect(() => {
        if (open) reset();
    }, [open, reset]);

    const ids = variables ?? userIds;
    const single = ids.length === 1;
    const question = `Are you sure you want to delete ${single ? "this user" : "these users"}?`;

    const requestClose = () => {
        if (isPending) return;
        onOpenChange(false);
    };

    return (
        <Modal
            open={open}
            onClose={requestClose}
            title={single ? "Delete user?" : `Delete ${ids.length} users?`}
            maxWidth="xs"
            isLoading={isPending}
            cancelLabel={single ? "No, Keep It" : "No, Keep Them"}
            error={
                isError
                    ? `The ${single ? "user" : "users"} could not be deleted. Please try again later.`
                    : null
            }
            actions={[
                {
                    label: single ? "Delete User" : "Delete Users",
                    onClick: () => {
                        if (!isPending) mutate(userIds);
                    },
                    variant: "contained",
                    color: "error",
                    disabled: isPending || userIds.length === 0,
                },
            ]}
        >
            <p className="text-sm">{question}</p>
        </Modal>
    );
}
